import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Users, Mail, ArrowLeft, ShieldCheck, ShoppingBag } from "lucide-react";
import { api, formatNaira } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Admin() {
  const { user, checking } = useAuth();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({ waitlist_count: 0, member_count: 0 });
  const [error, setError] = useState("");

  useEffect(() => {
    if (checking) return;
    if (!user || user.role !== "admin") {
      navigate("/login");
      return;
    }
    Promise.all([api.get("/admin/waitlist"), api.get("/admin/stats"), api.get("/admin/orders")])
      .then(([w, s, o]) => { setEntries(w.data); setStats(s.data); setOrders(o.data); })
      .catch(() => setError("Failed to load admin data."));
  }, [user, checking, navigate]);

  return (
    <div className="min-h-screen bg-[#050806] text-white" data-testid="admin-page">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-emerald-400 mb-8 font-accent tracking-wider">
          <ArrowLeft size={16} /> BACK TO SITE
        </Link>
        <div className="flex items-center gap-3 mb-10">
          <ShieldCheck className="text-amber-500" size={32} />
          <h1 className="font-display text-5xl">ADMIN DASHBOARD</h1>
        </div>

        {error && <div className="bg-red-500/10 border border-red-500/30 text-red-300 rounded-md px-4 py-3 mb-6">{error}</div>}

        <div className="grid sm:grid-cols-3 gap-5 mb-10">
          <div className="rounded-xl border border-white/10 bg-white/5 p-6" data-testid="stat-waitlist">
            <div className="flex items-center gap-3 text-emerald-400"><Mail size={22} /><span className="font-accent text-xl tracking-wider">WAITLIST</span></div>
            <p className="font-display text-6xl mt-2">{stats.waitlist_count}</p>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/5 p-6" data-testid="stat-members">
            <div className="flex items-center gap-3 text-amber-500"><Users size={22} /><span className="font-accent text-xl tracking-wider">MEMBERS</span></div>
            <p className="font-display text-6xl mt-2">{stats.member_count}</p>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/5 p-6" data-testid="stat-orders">
            <div className="flex items-center gap-3 text-emerald-300"><ShoppingBag size={22} /><span className="font-accent text-xl tracking-wider">ORDERS</span></div>
            <p className="font-display text-6xl mt-2">{orders.length}</p>
          </div>
        </div>

        <h2 className="font-display text-3xl mb-4">SIGNUPS</h2>
        <div className="rounded-xl border border-white/10 overflow-hidden" data-testid="waitlist-table">
          <table className="w-full text-left">
            <thead className="bg-white/5 text-gray-400 font-accent tracking-wider text-sm">
              <tr>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Source</th>
                <th className="px-4 py-3">Date</th>
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-500">No signups yet.</td></tr>
              )}
              {entries.map((e) => (
                <tr key={e.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`waitlist-row-${e.id}`}>
                  <td className="px-4 py-3">{e.email}</td>
                  <td className="px-4 py-3 text-gray-400">{e.name || "—"}</td>
                  <td className="px-4 py-3"><span className="text-xs bg-emerald-500/10 text-emerald-300 px-2 py-1 rounded">{e.source}</span></td>
                  <td className="px-4 py-3 text-gray-500 text-sm">{e.created_at ? new Date(e.created_at).toLocaleString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h2 className="font-display text-3xl mb-4 mt-12">ORDERS</h2>
        <div className="rounded-xl border border-white/10 overflow-x-auto" data-testid="orders-table">
          <table className="w-full text-left min-w-[720px]">
            <thead className="bg-white/5 text-gray-400 font-accent tracking-wider text-sm">
              <tr>
                <th className="px-4 py-3">Ref</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Items</th>
                <th className="px-4 py-3">Total</th>
                <th className="px-4 py-3">Method</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Date</th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">No orders yet.</td></tr>
              )}
              {orders.map((o) => (
                <tr key={o.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`order-row-${o.reference}`}>
                  <td className="px-4 py-3 font-mono text-xs text-emerald-400">{o.reference}</td>
                  <td className="px-4 py-3 text-sm">{o.customer_name}<br /><span className="text-gray-500 text-xs">{o.phone}</span></td>
                  <td className="px-4 py-3 text-xs text-gray-400">{o.items?.reduce((n, i) => n + i.quantity, 0)} item(s)</td>
                  <td className="px-4 py-3 text-sm">{formatNaira(o.total)}</td>
                  <td className="px-4 py-3"><span className="text-xs bg-white/10 px-2 py-1 rounded">{o.method}</span></td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded ${o.status === "paid" ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}`}>{o.status}</span></td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{o.created_at ? new Date(o.created_at).toLocaleString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
